import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    deleteNotification,
    getNotifications,
    markAllAsRead,
    markAsRead,
    NotificationItem,
    parseServerDate,
    stripEmoji,
} from "../../services/notification-service"
import { LoadingComponent } from "../../components/loading"

const TYPE_ICON: Record<string, string> = {
    NEW_APPLICATION: "📩",
    INVITATION_ACCEPTED: "✅",
    INVITATION_REFUSED: "❌",
    INVITATION_EXPIRED: "⌛",
    APPLICATION_CANCELLED: "🚫",
    FEEDBACK_REMINDER: "🌟",
}

function formatDate(iso: string): string {
    const d = parseServerDate(iso)
    const diff = Date.now() - d.getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return "acum"
    if (minutes < 60) return `${minutes} min în urmă`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h în urmă`
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" })
}

export const NotificationsPage = () => {
    const navigate = useNavigate()
    const [notifications, setNotifications] = useState<NotificationItem[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        getNotifications()
            .then(setNotifications)
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [])

    const unreadCount = notifications.filter((n) => !n.read).length

    const handleMarkAll = async () => {
        await markAllAsRead().catch(() => {})
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    }

    const handleDelete = async (e: React.MouseEvent, n: NotificationItem) => {
        e.stopPropagation()
        await deleteNotification(n.id).catch(() => {})
        setNotifications((prev) => prev.filter((x) => x.id !== n.id))
    }

    const handleClick = async (n: NotificationItem) => {
        if (!n.read) {
            await markAsRead(n.id).catch(() => {})
            setNotifications((prev) =>
                prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))
            )
        }
        if (n.applicationId) {
            navigate(`/applications/${n.applicationId}`)
        }
    }

    if (loading) return <LoadingComponent />

    return (
        <div className="w-full px-3.5 py-3.5 sm:px-5 sm:py-5">
            <div className="flex items-start justify-between mb-1">
                <div>
                    <h3 className="page-title">Notificări</h3>
                    <p className="page-description text-sm">
                        {unreadCount > 0 ? `${unreadCount} necitite` : "Toate citite"}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={handleMarkAll}
                        className="mt-2 text-sm font-semibold text-figmaPrimary hover:underline"
                    >
                        Marchează toate
                    </button>
                )}
            </div>

            {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24">
                    <p className="text-figmaDarkGray text-[14px] text-center">
                        Nicio notificare momentan
                    </p>
                </div>
            ) : (
                <div className="mt-4">
                    {notifications.map((n) => (
                        <div
                            key={n.id}
                            className="candidate-item mt-[12px] sm:mt-[16px] cursor-pointer"
                            style={!n.read ? { borderLeft: "3px solid #486af6" } : {}}
                            onClick={() => handleClick(n)}
                        >
                            <div className="flex items-start gap-3">
                                <span className="text-2xl leading-none mt-0.5 shrink-0">
                                    {TYPE_ICON[n.type] ?? "🔔"}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="candidate-feature-title text-[15px]">
                                            {stripEmoji(n.title)}
                                        </p>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {!n.read && (
                                                <span className="h-2 w-2 rounded-full bg-figmaContrast" />
                                            )}
                                            <button
                                                type="button"
                                                aria-label="Șterge notificarea"
                                                onClick={(e) => handleDelete(e, n)}
                                                className="text-figmaDarkGray hover:text-figmaContrast"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-4 w-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-figmaDarkGray text-[13px] mt-1">
                                        {n.message}
                                    </p>
                                    <p className="text-[11px] mt-1.5" style={{ color: "#bbb" }}>
                                        {formatDate(n.createdAt)}
                                    </p>
                                </div>
                                {n.applicationId && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 shrink-0 mt-1"
                                        style={{ color: "#ccc" }}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
